import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Check, Mail, Clock, MoreHorizontal, Loader2 } from 'lucide-react';
import type { UserJourneyProgress } from '../../types/klanttraject';

interface Props {
  userId: string;
  refreshKey?: number;
}

interface MilestoneStatus {
  key: 'first_blog' | 'connect_social' | 'first_post' | 'import_list' | 'first_newsletter';
  label: string;
  achieved: boolean;
  achievedAt: string | null;
  nudgeStepKey: string;
  nudgeStatus: 'pending' | 'scheduled' | 'completed' | 'skipped' | 'pending_manual' | 'failed' | 'paused' | 'none';
  nudgeCompletedReason: string | null;
  nudgeScheduledFor: string | null;
  nudgeCompletedAt: string | null;
}

const MILESTONES = [
  { key: 'first_blog', label: 'Eerste blog', nudgeStepKey: 'first_blog_nudge' },
  { key: 'connect_social', label: 'Social gekoppeld', nudgeStepKey: 'connect_social_nudge' },
  { key: 'first_post', label: 'Eerste social post', nudgeStepKey: 'first_social_post_nudge' },
  { key: 'import_list', label: 'Mailinglijst', nudgeStepKey: 'import_mailinglist_nudge' },
  { key: 'first_newsletter', label: 'Eerste nieuwsbrief', nudgeStepKey: 'first_newsletter_nudge' },
] as const;

export function JourneyMilestonesBlock({ userId, refreshKey }: Props) {
  const [statuses, setStatuses] = useState<MilestoneStatus[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [blogs, posts, contacts, campaigns, user, progressRes] = await Promise.all([
        supabase.from('published_blogs').select('id, created_at').eq('user_id', userId).order('created_at', { ascending: true }).limit(1).maybeSingle(),
        supabase.from('social_media_posts').select('id, created_at').eq('user_id', userId).order('created_at', { ascending: true }).limit(1).maybeSingle(),
        supabase.from('email_contacts').select('id, created_at').eq('user_id', userId).order('created_at', { ascending: true }).limit(1).maybeSingle(),
        supabase.from('email_campaigns').select('id, created_at').eq('user_id', userId).eq('status', 'completed').order('created_at', { ascending: true }).limit(1).maybeSingle(),
        supabase.from('social_profiles').select('connected_platforms').eq('user_id', userId).maybeSingle(),
        supabase.from('user_journey_progress')
          .select('step_key, status, completed_reason, completed_at, scheduled_for')
          .eq('user_id', userId)
          .in('step_key', MILESTONES.map(m => m.nudgeStepKey)),
      ]);
      if (cancelled) return;

      const progressByKey = new Map<string, UserJourneyProgress>(
        (progressRes.data ?? []).map((p: any) => [p.step_key, p as UserJourneyProgress])
      );

      const connected = (user.data?.connected_platforms ?? {}) as Record<string, { handle?: string; reauth_required?: boolean } | '' | undefined>;
      const social_connected = ['facebook', 'linkedin', 'instagram'].some((p) => {
        const a = connected[p];
        return !!(a && typeof a === 'object' && a.handle && !a.reauth_required);
      });

      const out: MilestoneStatus[] = MILESTONES.map(m => {
        const p = progressByKey.get(m.nudgeStepKey);
        let achieved = false;
        let achievedAt: string | null = null;
        if (m.key === 'first_blog' && blogs.data) { achieved = true; achievedAt = blogs.data.created_at; }
        else if (m.key === 'connect_social' && social_connected) { achieved = true; }
        else if (m.key === 'first_post' && posts.data) { achieved = true; achievedAt = posts.data.created_at; }
        else if (m.key === 'import_list' && contacts.data) { achieved = true; achievedAt = contacts.data.created_at; }
        else if (m.key === 'first_newsletter' && campaigns.data) { achieved = true; achievedAt = campaigns.data.created_at; }

        return {
          key: m.key,
          label: m.label,
          achieved,
          achievedAt,
          nudgeStepKey: m.nudgeStepKey,
          nudgeStatus: (p?.status ?? 'none') as MilestoneStatus['nudgeStatus'],
          nudgeCompletedReason: p?.completed_reason ?? null,
          nudgeScheduledFor: p?.scheduled_for ?? null,
          nudgeCompletedAt: p?.completed_at ?? null,
        };
      });

      setStatuses(out);
    })();
    return () => { cancelled = true; };
  }, [userId, refreshKey]);

  if (!statuses) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-4 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Milestones laden...
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">Activatie-milestones</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {statuses.map(s => <Tile key={s.key} status={s} />)}
      </div>
    </div>
  );
}

function Tile({ status }: { status: MilestoneStatus }) {
  let icon: React.ReactNode;
  let color: string;
  let line1: string;
  let line2: string;

  if (status.achieved) {
    icon = <Check className="w-4 h-4" />;
    color = 'bg-emerald-50 border-emerald-200 text-emerald-800';
    line1 = 'Door user zelf';
    line2 = status.achievedAt
      ? new Date(status.achievedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
      : '';
  } else if (status.nudgeStatus === 'completed' && status.nudgeCompletedReason === 'cron') {
    icon = <Mail className="w-4 h-4" />;
    color = 'bg-blue-50 border-blue-200 text-blue-800';
    line1 = 'Nudge verstuurd';
    line2 = status.nudgeCompletedAt
      ? new Date(status.nudgeCompletedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
      : '';
  } else if (status.nudgeStatus === 'scheduled') {
    icon = <Clock className="w-4 h-4" />;
    color = 'bg-amber-50 border-amber-200 text-amber-800';
    line1 = 'Nudge gepland';
    line2 = status.nudgeScheduledFor
      ? new Date(status.nudgeScheduledFor).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })
      : '';
  } else if (status.nudgeStatus === 'skipped') {
    icon = <Check className="w-4 h-4" />;
    color = 'bg-slate-50 border-slate-200 text-slate-700';
    line1 = 'Overgeslagen';
    line2 = '';
  } else {
    icon = <MoreHorizontal className="w-4 h-4" />;
    color = 'bg-slate-50 border-slate-200 text-slate-500';
    line1 = 'In wachtrij';
    line2 = '';
  }

  return (
    <div className={`rounded-md border p-2 ${color}`}>
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <div className="text-xs font-medium truncate">{status.label}</div>
      </div>
      <div className="text-[11px] leading-tight opacity-80">{line1}</div>
      {line2 && <div className="text-[10px] leading-tight opacity-60">{line2}</div>}
    </div>
  );
}
